import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

const STATUS = {
  imported: ["bg-emerald-50 text-emerald-700 ring-emerald-200", "imported"],
  no_cost_code: ["bg-amber-50 text-amber-700 ring-amber-200", "no cost code"],
  blank: ["bg-slate-100 text-slate-500 ring-slate-200", "blank"],
  formulas: ["bg-red-50 text-red-700 ring-red-200", "formulas"],
  headers: ["bg-red-50 text-red-700 ring-red-200", "headers"],
  no_tab: ["bg-slate-100 text-slate-400 ring-slate-200", "no tab"],
};
const money = (v) => (v === null || v === undefined ? "—"
  : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

/** Recognition checks, formula-evaluation counts and the per-tab read/imported/flagged table. */
export function FrsImportSummary({ preview }) {
  const ev = preview.evaluation || {};
  const scopes = preview.scopes || [];
  return (
    <div className="space-y-2" data-testid="frs-import-summary">
      {preview.empty && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs font-medium text-amber-800"
             data-testid="frs-empty-message">
          {preview.message}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500"
           data-testid="frs-recognition">
        {(preview.recognition || []).map((c) => (
          <span key={c.check} className="inline-flex items-center gap-1" title={c.detail || c.check}>
            {c.ok ? <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                  : c.required ? <XCircle className="h-3 w-3 text-red-600" />
                               : <AlertTriangle className="h-3 w-3 text-amber-600" />}
            {c.check.split(" with ")[0].split(" tabs")[0]}
            {!c.ok && c.detail && <span className="text-slate-400">— {c.detail}</span>}
          </span>
        ))}
        <span className="ml-auto tabular-nums" data-testid="frs-evaluation"
              title="Cells read from the workbook's saved results vs. formulas this importer had to work out itself">
          {ev.cells_cached || 0} cached · {ev.cells_evaluated || 0} evaluated
          {ev.cells_failed ? <span className="font-medium text-red-600"> · {ev.cells_failed} could not be evaluated</span> : ""}
        </span>
      </div>
      <div className="overflow-auto rounded border border-slate-200">
        <table className="w-full text-[11px]" data-testid="frs-tab-summary">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="px-2 py-1 text-left font-medium">Tab</th>
              <th className="px-2 py-1 text-left font-medium">Ops code</th>
              <th className="px-2 py-1 text-right font-medium">Read</th>
              <th className="px-2 py-1 text-right font-medium">Imported</th>
              <th className="px-2 py-1 text-right font-medium">Flagged</th>
              <th className="px-2 py-1 text-left font-medium">Result</th>
            </tr>
          </thead>
          <tbody>
            {scopes.map((s) => {
              const [cls, label] = STATUS[s.status] || STATUS.blank;
              return (
                <tr key={s.scope} className="border-t border-slate-100" data-testid={`frs-scope-${s.scope}`}>
                  <td className="px-2 py-1 font-medium text-slate-700">
                    {s.tab || s.scope}
                    {s.spec_section && <span className="ml-1 font-normal text-slate-400">{s.spec_section}</span>}
                  </td>
                  <td className="px-2 py-1 font-mono text-slate-500">
                    {s.cost_code || <span className="font-sans text-amber-700">unmapped</span>}
                    {s.section && s.cost_code && <span className="ml-1 font-sans text-slate-400">{s.section}</span>}
                  </td>
                  <td className="px-2 py-1 text-right tabular-nums">{s.rows_read ?? s.products}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{s.imported}</td>
                  <td className={`px-2 py-1 text-right tabular-nums ${s.rows_flagged ? "font-medium text-amber-700" : ""}`}>
                    {s.rows_flagged || 0}
                  </td>
                  <td className="px-2 py-1">
                    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ${cls}`}>{label}</span>
                    {s.reason && <span className="ml-1.5 text-slate-500">{s.reason}</span>}
                  </td>
                </tr>
              );
            })}
            {preview.quotes && (
              <tr className="border-t border-slate-100" data-testid="frs-scope-QUOTES">
                <td className="px-2 py-1 font-medium text-slate-700">{preview.quotes.tab || "QUOTES"}</td>
                <td className="px-2 py-1 text-slate-400">vendor quotes</td>
                <td className="px-2 py-1 text-right tabular-nums">{preview.quotes.read || 0}</td>
                <td className="px-2 py-1 text-right tabular-nums">{(preview.quotes.rows || []).length}</td>
                <td className="px-2 py-1 text-right tabular-nums">
                  {(preview.quotes.rows || []).filter((q) => (q.flags || []).length).length}
                </td>
                <td className="px-2 py-1">
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ${
                    (preview.quotes.rows || []).length ? STATUS.imported[0] : STATUS.blank[0]}`}>
                    {(preview.quotes.rows || []).length ? "imported" : "blank"}
                  </span>
                  {preview.quotes.reason && <span className="ml-1.5 text-slate-500">{preview.quotes.reason}</span>}
                  {preview.quotes.lines_attached > 0 && (
                    <span className="ml-1.5 text-slate-500">{preview.quotes.lines_attached} unit price(s) from QUOTE LINES</span>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {preview.labor && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-600" data-testid="frs-labor-summary"
             title="Labor hours per line: cost-library part first, else scope → keyword → STD LABOR item">
          <span className="font-medium text-slate-700">Labor:</span>
          <span>{preview.labor.part} from part</span>
          <span>{preview.labor.keyword} by keyword</span>
          {preview.labor.scope_default > 0 && <span className="text-amber-700">{preview.labor.scope_default} by scope default</span>}
          {preview.labor.none > 0 && <span className="font-medium text-red-600">{preview.labor.none} unresolved — zero hours, flagged</span>}
        </div>)}
      {preview.crew_days?.present && (
        <div className="overflow-auto rounded border border-slate-200" data-testid="frs-crew-days">
          <div className="flex items-center gap-2 bg-slate-50 px-2 py-1 text-[11px] text-slate-600">
            <span className="font-medium text-slate-700">CREW &amp; DAYS</span>
            <span>{preview.crew_days.hours_per_day}-h man-day · {preview.crew_days.cells_cached} cached · {preview.crew_days.cells_computed} computed</span>
            <span className="text-slate-400">→ crew / days / mobs land on the estimate and fill the (x) quals lines</span>
          </div>
          <table className="w-full text-[11px]">
            <thead className="text-[10px] uppercase tracking-wide text-slate-500"><tr>
              <th className="px-2 py-1 text-left">Scope</th><th className="px-2 py-1 text-right">Install hrs</th><th className="px-2 py-1 text-right">Crew</th>
              <th className="px-2 py-1 text-right">Install days</th><th className="px-2 py-1 text-right">Delivery hrs</th><th className="px-2 py-1 text-right">Days on site</th>
              <th className="px-2 py-1 text-right">Mobs</th><th className="px-2 py-1 text-left">Source</th></tr></thead>
            <tbody>
              {Object.entries(preview.crew_days.scopes).map(([code, c]) => (
                <tr key={code} className="border-t border-slate-100" data-testid={`frs-crew-${code}`}>
                  <td className="px-2 py-1 font-medium">{code}</td><td className="px-2 py-1 text-right tabular-nums">{c.install_hrs}</td>
                  <td className={`px-2 py-1 text-right tabular-nums ${c.crew ? "" : "font-medium text-amber-700"}`}>{c.crew ?? "blank"}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{c.install_days ?? "—"}</td><td className="px-2 py-1 text-right tabular-nums">{c.delivery_hrs}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{c.days_on_site ?? "—"}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{c.mobs}{c.mobs_defaulted ? "*" : ""}</td>
                  <td className="px-2 py-1 text-slate-500">{c.source}{c.flag ? <span className="ml-1 text-amber-700">· {c.flag}</span> : ""}</td>
                </tr>))}
            </tbody>
          </table>
        </div>)}
    </div>
  );
}

/** QUOTES tab rows as they will land: scope, vendor, #, dates, money, coverage decision, flags. */
export function FrsQuotesTable({ quotes }) {
  return (
    <div className="overflow-auto rounded border border-sky-200" data-testid="frs-quotes-table">
      <table className="w-full text-[11px]">
        <thead className="bg-sky-50 text-sky-800">
          <tr>
            <th className="px-2 py-1 text-left font-medium">Scope</th>
            <th className="px-2 py-1 text-left font-medium">Vendor</th>
            <th className="px-2 py-1 text-left font-medium">Quote #</th>
            <th className="px-2 py-1 text-left font-medium">Expires</th>
            <th className="px-2 py-1 text-right font-medium">Cost</th>
            <th className="px-2 py-1 text-right font-medium">Freight</th>
            <th className="px-2 py-1 text-right font-medium">Misc</th>
            <th className="px-2 py-1 text-right font-medium">TOTAL</th>
            <th className="px-2 py-1 text-left font-medium">Coverage · pricing</th>
          </tr>
        </thead>
        <tbody>
          {quotes.rows.map((q) => (
            <tr key={`${q.scope}-${q.vendor}-${q.quote_ref}`} className="border-t border-sky-100"
                data-testid={`frs-quote-${q.quote_ref || q.row}`}>
              <td className="px-2 py-1 font-medium text-slate-700">{q.scope || "—"}
                {q.section && q.section !== q.scope && <div className="text-[10px] font-normal text-slate-400">{q.section}</div>}
              </td>
              <td className="px-2 py-1 text-slate-700">
                {q.vendor}
                {q.vendor_matched_name && q.vendor_matched_name !== q.vendor && (
                  <div className="text-[10px] text-slate-400">→ {q.vendor_matched_name}</div>
                )}
              </td>
              <td className="px-2 py-1 font-mono text-slate-600">{q.quote_ref || "—"}</td>
              <td className={`px-2 py-1 ${q.expired ? "font-medium text-red-600" : "text-slate-600"}`}>
                {q.valid_through || "—"}{q.expired ? " (expired)" : ""}
              </td>
              <td className="px-2 py-1 text-right tabular-nums">{money(q.quote_cost)}</td>
              <td className="px-2 py-1 text-right tabular-nums">{q.freight ? money(q.freight) : "—"}</td>
              <td className="px-2 py-1 text-right tabular-nums">{q.misc ? money(q.misc) : "—"}</td>
              <td className="px-2 py-1 text-right font-medium tabular-nums">{money(q.amount)}</td>
              <td className="max-w-[18rem] px-2 py-1">
                <div className="text-slate-600" title={q.coverage}>{q.coverage}</div>
                <div className="text-[10px] text-sky-700">price by {q.price_mode}</div>
                {(q.flags || []).map((f) => (
                  <div key={f} className="text-[10px] font-medium text-amber-700" data-testid={`frs-quote-flag-${q.quote_ref || q.row}`}>{f}</div>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
